import { ChangeDetectorRef, Component } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TableAction, TableColumn } from '../../../shared/models/table-models';
import { Table } from '../../../shared/table/table';
import { AppIcons } from '../../../../core/icons/fa-icons';
import { Popup } from '../../../../core/shared/components/popup/popup';
import { DynamicFieldConfig } from '../../../../core/shared/dynamic-form/form-config.model';
import { PKL_GRP } from '../../../../core/shared/models/picklist.model';
import { PickListService } from '../../../../core/shared/services/picklist.service';
import { Setup } from '../../shared/service/setup';
import { BackButton } from "../../../shared/buttons/back-button";

@Component({
  selector: 'app-incom-value',
  imports: [TranslatePipe, Table, Popup, BackButton],
  templateUrl: './incom-value.html',
  styleUrl: './incom-value.css',
})
export class IncomValue {
  icons = AppIcons;

  stmntTypSeq!: number;

  list: any[] = [];
  columns: TableColumn[] = [];
  fields: DynamicFieldConfig[] = [];

  showPopup = false;
  isEditMode = false;
  isViewMode = false;
  formData: any = {};

  showDeletePopup = false;
  deleteMessage = '';
  selectedRow: any = null;

  constructor(
    private translate: TranslateService,
    private setup: Setup,
    private toastr: ToastrService,
    private picklist: PickListService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}




ngOnInit(): void {
  this.stmntTypSeq = Number(
    this.route.snapshot.paramMap.get('stmntTypSeq')
  );

  if (!this.stmntTypSeq) {
    this.toastr.error(this.translate.instant('SETUP.INCOME.TYPE_NOT_FOUND'));
    this.router.navigate(['/income-statement-type']);
    return;
  }

  this.loadPicklists();
}



  // ================= PICKLIST =================
  loadPicklists(): void {
    this.picklist.loadCodes([PKL_GRP.COMMON_STATUS]);

    this.picklist.picklists$.pipe(take(1)).subscribe(() => {
      this.buildFields();
      this.buildColumns();
      this.loadData();
    });
  }

  // ================= FORM =================
 buildFields(): void {
  this.fields = [
    {
      key: 'stmntValSeq',
      label: 'SETUP.ID',
      type: 'text',
      hidden: true,
    },
    {
      key: 'sortOrdr',
      label: 'SETUP.SORT_ORDER',
      type: 'number',
      validations: { required: true },
    },
    {
      key: 'status',
      label: 'COMMON.STATUS',
      type: 'select',
      options$: this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS),
      validations: { required: true },
    },
    {
      key: 'dscr', // changed from description
      label: 'COMMON.DESCRIPTION',
      type: 'textarea',
      validations: { required: true },
    },
  ];
}

  // ================= TABLE =================
  buildColumns(): void {
    this.columns = [
      { key: 'stmntValSeq', label: 'SETUP.ID' },
      { key: 'description', label: 'COMMON.DESCRIPTION' },
      { key: 'sortOrdr', label: 'SETUP.SORT_ORDER' },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'badge',
        map: this.getStatusMap(),
        badgeMap: this.getStatusBadgeMap(),
      },
    ];
  }

  getStatusMap(): Record<number, string> {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    const map: Record<number, string> = {};

    options.forEach((option) => {
      map[option.value] = option.label;
    });

    return map;
  }

  getStatusBadgeMap(): Record<number, string> {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    // Rows always carry 706 (active) / 707 (inactive), so the pill colour must not depend on the picklist label text.
    const map: Record<number, string> = { 706: 'status-active', 707: 'status-inactive' };

    options.forEach((option) => {
      const label = option.label?.trim().toLowerCase();

      if (label === 'active') {
        map[option.value] = 'status-active';
      } else if (label === 'inactive') {
        map[option.value] = 'status-inactive';
      } else if (!map[option.value]) {
        map[option.value] = 'status-default';
      }
    });

    return map;
  }

  // ================= ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.icons.edit,
      tooltip: 'COMMON.EDIT',
      class: 'action-edit',
      onClick: (row) => this.openPopup(row),
    },
    {
      icon: this.icons.view,
      tooltip: 'COMMON.VIEW',
      class: 'action-view',
      onClick: (row) => this.openPopup(row, true),
    },
    {
      icon: this.icons.delete,
      tooltip: 'COMMON.DELETE',
      class: 'action-delete',
      onClick: (row) => this.openDeletePopup(row),
    },
  ];

  // ================= DELETE CONFIRM =================
  openDeletePopup(row: any) {
    this.selectedRow = row;
    this.deleteMessage = this.translate.instant('SETUP.CONFIRM_DELETE_NAME', { name: row.description });
    this.showDeletePopup = true;
  }

  confirmDelete(): void {
    if (!this.selectedRow) return;
    this.delete(this.selectedRow);
    this.selectedRow = null;
  }

  // ================= LOAD =================
  loadData(): void {
    this.setup.getIncomeStatementValues(this.stmntTypSeq).subscribe({
      next: (res: any[]) => {
        this.list = (res || []).map((item) => ({
          stmntValSeq: item.stmntValSeq,
          name: item.valNm,
          description: item.dscr,
          sortOrdr: item.sortOrdr,
          status: item.actvFlg ? 706 : 707,
        }));
      },
      error: () => {
        this.toastr.error(this.translate.instant('SETUP.INCOME.LOAD_VALUES_FAILED'));
      },
    });
  }

  // ================= POPUP =================
openPopup(data?: any, viewMode: boolean = false): void {
  this.showPopup = true;
  this.isViewMode = viewMode;

  if (data) {
    this.isEditMode = true;

    this.formData = {
      stmntValSeq: data.stmntValSeq,
      dscr: data.description,
      sortOrdr: data.sortOrdr,
      status: data.status,
    };

    return;
  }

  this.isEditMode = false;

  this.formData = {
    stmntValSeq: null, // first-time save must be null
    dscr: '',
    sortOrdr: this.list.length + 1,
    status: this.getDefaultStatus(),
  };
}


  getDefaultStatus(): number {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    return options?.[0]?.value || 706;
  }

  // ================= SAVE =================
handleSave = (form: any) => {
  const payload = {
    stmntValSeq: form.stmntValSeq ?? null,
    stmntTypSeq: this.stmntTypSeq, // route sequence
    actvFlg: Number(form.status) === 706,
    dscr: form.dscr?.trim() || '',
    sortOrdr: Number(form.sortOrdr) || 1,
    delFlg: 0,
  };

  return this.setup.saveIncomeStatementValue(payload).subscribe({
    next: () => {
      this.toastr.success(
        this.translate.instant(this.isEditMode
          ? 'SETUP.INCOME.VALUE_UPDATED'
          : 'SETUP.INCOME.VALUE_CREATED')
      );

      this.showPopup = false;
      this.loadData();
    },
    error: () => {
      this.toastr.error(this.translate.instant('SETUP.INCOME.SAVE_VALUE_FAILED'));
    },
  });
};

delete(row: any): void {
  const payload = {
    stmntValSeq: row.stmntValSeq,
    stmntTypSeq: this.stmntTypSeq,
    actvFlg: Number(row.status) === 706,
    dscr: row.description || '',
    sortOrdr: Number(row.sortOrdr) || 1,
    delFlg: 1,
  };

  this.setup.saveIncomeStatementValue(payload).subscribe({
    next: () => {
      this.toastr.success(this.translate.instant('SETUP.INCOME.VALUE_DELETED'));

      // UI se immediately remove
      this.list = this.list.filter(
        (item) => item.stmntValSeq !== row.stmntValSeq
      );

      this.cdr.detectChanges();

      // Backend se latest list bhi load kar lo
      this.loadData();
    },
    error: () => {
      this.toastr.error(this.translate.instant('SETUP.INCOME.DELETE_VALUE_FAILED'));
    },
  });
}


 

}
